/**
 * The monsters' shots, one main-loop pass each (02 §5), as the software-sprite loop D_0CF2 runs types
 * $24..$33 through its table at $0D68: $28 the fireball (D_7C14), $2A the rock (L1274), $2C the bottle
 * (oscillating_enemy $12B4), $32 the laser (L12F9). Each moves a whole cell, then L152B kills the
 * first player (player 2 first) in its box; a shot keeps going after a kill, except the laser.
 *
 * - Under the clock ($67 set) the loop skips every one of these types: shots stand and harm nobody.
 * - At the level clear the sweep at $171B turns every shot into a burst ($3A).
 * - A player's death does nothing to them (no routine touches the shots then); L152B simply skips a
 *   player who is dying or respawning (states >= $0D) - and one under the power heart (state $18).
 *
 * The box, for the renderer: a shot's `x`, `y` are the top-left of its 16x16 picture in the VIC frame
 * (playfield pixels x - 20, y - 45, as a bubble's); it kills within |Px - (x + 4)| < 16, |Py - y| < 16.
 */

import { carriedByWater } from '../bubbles';
import { killPlayer } from '../players';
import { solidTile } from '../playfield';
import {
  ATTACK, BURST_PASSES, LASER, SHOT_BASE_X, SHOT_BASE_Y, SHOT_HIT_X, SHOT_REACH, VANISH_PASSES,
} from './tuning';
import type { Projectile, World } from '../types';

/**
 * Is the map cell `dcol` columns from the shot's, beside its lower half, solid? D_7BFE points at map
 * row `row - 4` (a shot's row is the tile row + 3, as a bubble's); + $4F / + $52 is two rows down, one
 * column left / two right. Rows off the map read the edge rows (D_3A6C).
 */
function wall(world: World, p: Projectile, dcol: number): boolean {
  const m = p.motion;
  return solidTile(world.playfield, m.col + dcol, Math.max(0, Math.min(24, m.row - 2)));
}

function place(p: Projectile): void {
  p.x = (p.motion.col * 8 + SHOT_BASE_X) & 0xff;
  p.y = (p.motion.row * 8 + SHOT_BASE_Y) & 0xff;
}

/** It stops here: a burst ($3A) or a vanish ($38). */
function stop(p: Projectile, burst: boolean): void {
  p.motion.end = 0;
  p.motion.burst = burst;
}

/**
 * L152B: kill the first player in play (player 2 first) within the shot's box. True if one died. It
 * takes sprite states 1..$0C: not a player the water carries ($0D, set by the catch loop L_1BD2). (The
 * Baron runs the same test widened to $0D, `dec $1545` / `inc $1549` at $1496, so it still catches one.)
 */
function hitPlayer(world: World, p: Projectile): boolean {
  if (world.power.heart >= 0) return false;
  const cx = (p.motion.col * 8 + SHOT_HIT_X) & 0xff;
  const cy = (p.motion.row * 8 + SHOT_BASE_Y) & 0xff;
  for (const id of [1, 0] as const) {
    const pl = world.players[id];
    if (pl.state !== 'play' || pl.invulnerable > 0 || carriedByWater(world, id)) continue;
    if (Math.abs(pl.x - cx) < SHOT_REACH && Math.abs(pl.y - cy) < SHOT_REACH) {
      killPlayer(world, id);
      return true;
    }
  }
  return false;
}

/**
 * L1288: one cell along its way, then the players, unless the cell beyond it (beside its lower half)
 * is solid: then it does not move (L12AB) and the caller stops or turns it. False when a wall stopped it.
 */
function roll(world: World, p: Projectile): boolean {
  if (wall(world, p, p.dir < 0 ? -1 : 2)) return false;
  p.motion.col = (p.motion.col + p.dir) & 0xff;
  place(p);
  hitPlayer(world, p);
  return true;
}

/** D_7C14: the fireball flickers and flies a cell a pass; a wall makes it vanish ($38). */
function fireball(world: World, p: Projectile): void {
  p.motion.anim ^= 1;
  if (!roll(world, p)) stop(p, false);
}

/** L1274: the rock turns over every pass and rolls on every other one; at a wall it crumbles ($3A). */
function rock(world: World, p: Projectile): void {
  const m = p.motion;
  m.anim ^= 1;
  if (m.anim === (p.dir < 0 ? 1 : 0) && !roll(world, p)) stop(p, true);
}

/**
 * $12B4: the bottle flies a cell a pass, spinning; a wall turns it round where it is ($12D6: $3A back
 * to $2C, way flipped); back at the column it left from it vanishes ($38), and its Drunk may walk on
 * after a cooldown of 100 (L12DE).
 */
function bottle(world: World, p: Projectile): void {
  const m = p.motion;
  const moved = roll(world, p);
  m.anim = (m.anim + 1) & 3;
  if (!moved) p.dir = p.dir < 0 ? 1 : -1;
  if (m.col !== m.home) return;
  const owner = world.monsters.find((o) => o.id === p.owner);
  if (owner) {
    owner.ai.cooldown = ATTACK.bottleCooldown;
    owner.ai.attack = -1;
  }
  stop(p, false);
}

/**
 * L12F9: the laser drops a row a pass through everything. A kill stops it ($3A), as does the row
 * below the screen ($38); either way its Invader may fire again once its cooldown is out.
 */
function laser(world: World, p: Projectile): void {
  const m = p.motion;
  m.row = (m.row + 1) & 0xff;
  place(p);
  const killed = hitPlayer(world, p);
  if (!killed && m.row !== LASER.endRow) return;
  stop(p, killed);
  const owner = world.monsters.find((o) => o.id === p.owner);
  if (owner) owner.ai.attack = -1;
}

const PASS = { fireball, rock, bottle, laser } as const;

/** One main-loop pass of every shot, then the stopped ones play out their burst or vanish and go. */
export function projectilesPass(world: World): void {
  const clear = world.flow.phase === 'clear';
  const clock = world.power.clock > 0;
  for (const p of world.projectiles.slice()) {
    const m = p.motion;
    if (m.end >= 0) {
      m.end++;
      continue;
    }
    // The clock also holds back the level-clear sweep: it runs from the timer code the clock skips ($15C9).
    if (clock) continue;
    if (clear) {
      stop(p, true);
      continue;
    }
    m.age++;
    PASS[p.kind](world, p);
  }
  world.projectiles = world.projectiles.filter((p) => {
    const m = p.motion;
    return m.end < (m.burst ? BURST_PASSES : VANISH_PASSES);
  });
}

/** What the renderer draws of a shot. */
export interface ProjectileLook {
  /** Top-left of the 16x16 picture, VIC frame (playfield pixels x - 20, y - 45). */
  x: number;
  y: number;
  /**
   * The C64 picture: fireball 0..3 (bit 1 facing right, bit 0 its two-frame flicker), rock 0/1 (its
   * roll), bottle 0..3 (its spin), laser 0.
   */
  frame: number;
  /** -1 whole; 0 the first burst picture, 1 the second (a crumbling rock, a laser's kill, the clear). */
  burst: -1 | 0 | 1;
  /** False once gone (a vanished fireball or bottle, the burst's last pass): draw nothing. */
  visible: boolean;
}

export function projectileLook(p: Projectile): ProjectileLook {
  const m = p.motion;
  const frame = p.kind === 'laser' ? 0 : p.kind === 'rock' ? m.anim & 1 : m.anim & 3;
  if (m.end <= 0) return { x: p.x, y: p.y, frame, burst: -1, visible: true };
  if (!m.burst) return { x: p.x, y: p.y, frame, burst: -1, visible: false };
  const k = m.end - 1;
  return { x: p.x, y: p.y, frame, burst: k < 2 ? 0 : 1, visible: k < 4 };
}
